/* eslint-disable react-refresh/only-export-components --
   This module intentionally exports the DeviceStreamProvider component
   alongside its companion hooks (useSharedDeviceStream, useDeviceLiveLogging,
   useDeviceMemoryMonitor, useDeviceStreamBufferCap). Co-location is the point
   of the provider; the rule only affects Fast Refresh in dev. */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDeviceStream } from '@/hooks/use-device-stream';
import {
  useEnableLiveLogging,
  useDisableLiveLogging,
  useEnableMemoryMonitor,
  useDisableMemoryMonitor,
} from '@/hooks/use-devices';
import { notificationStore } from '@/stores/notification.store';
import type {
  DeviceLog,
  DeviceStatusEvent,
  FeatureSessionEvent,
  FeatureType,
  LogLevel,
  MemoryReport,
} from '@/types';

/**
 * Shared device-stream context.
 *
 * Why this exists: previously every consumer (the device-detail page,
 * each tab inside it, the live dashboard) called useDeviceStream() and
 * spawned a separate WebSocket. With multiple consumers active at once
 * the backend hub fans out the same MQTT message to N clients, paying a
 * per-client copy and chewing extra bandwidth for no reason.
 *
 * The provider keeps a single WebSocket per deviceId. Consumers register
 * what they "want" (logs, memory) via useSharedDeviceStream; the
 * provider OR-aggregates those wants into the underlying useDeviceStream
 * options. When at least one consumer wants logs, the WS is told
 * set_logs=true; when none want it, set_logs=false. Same for memory.
 * Status/heartbeat is always on because the chip on the page header
 * needs it constantly.
 */

// Same-browser cross-tab announcements. The server relay (Redis feature:updates
// → WS) is the authoritative path and covers other browsers, other operators and
// TTL expiry — but it needs Redis wired and a round trip through the backend.
// Tabs in THIS browser can tell each other directly and instantly, which is the
// case people actually hit: two tabs open on the same device.
const FEATURE_CHANNEL_PREFIX = 'mdm-feature-session:';

// Both paths usually fire for the same Stop. Whichever arrives first wins and
// the other is dropped inside this window, so one Stop raises one notification.
// Short enough that a genuine stop → start → stop still gets through.
const ANNOUNCE_DEDUPE_MS = 3000;

const FEATURE_LABEL: Record<FeatureType, string> = {
  live_logging: 'Live logging',
  memory_monitor: 'Memory monitoring',
  activity_monitor: 'Activity monitoring',
};

/**
 * Wording for a session that ended under a watching tab.
 *
 * Keyed on `reason` rather than `disabled_by`: the enable/disable endpoints now
 * record the real operator, but older sessions carry a hardcoded "admin", so
 * naming a person could be a fabrication.
 *
 * "another tab" only when `local` proves it came from this browser; otherwise
 * "another session", which stays true whether it was a different browser or a
 * different operator.
 */
function featureStoppedMessage(e: FeatureSessionEvent): string {
  const what = FEATURE_LABEL[e.feature_type] ?? 'This stream';
  switch (e.reason) {
    case 'manual':
      return e.local
        ? `${what} was stopped in another tab — the stream has ended here too.`
        : `${what} was stopped in another session — the stream has ended.`;
    case 'expired':
      return `${what} timed out and stopped. Start it again to keep watching.`;
    case 'device_offline':
      return `${what} stopped because the device went offline.`;
    case 'server_shutdown':
      return `${what} stopped because the server restarted. Start it again to resume.`;
    case 'ws_disconnect':
      return `${what} stopped after the last viewer disconnected.`;
    default:
      return `${what} stopped and is no longer streaming.`;
  }
}

interface Wants {
  logs: boolean;
  memory: boolean;
  /** Watching the activity stream. Activity frames arrive under the `logs`
   *  gate, so this doesn't affect delivery and isn't sent to the server. It
   *  exists so the provider knows whether this tab is watching activity, and
   *  therefore whether an activity session ending is worth interrupting for. */
  activity: boolean;
}

interface LiveLoggingOptions {
  level?: LogLevel;
  patchSize?: number;
  patchTimeout?: number;
}

interface MemoryMonitorOptions {
  threshold?: number;
}

interface ContextValue {
  /** Snapshot accessor for the live log buffer. Backed by a ref inside
   *  useDeviceStream so the array never enters this context value —
   *  React 19's dev profiler would otherwise structured-clone it on
   *  every render and OOM the tab past ~10k rows. Re-read on each render
   *  inside consumers; pair with logsVersion as a memo dep. */
  getLogs: () => DeviceLog[];
  logsVersion: number;
  memoryReports: MemoryReport[];
  status: DeviceStatusEvent | null;
  isConnected: boolean;
  clearLogs: () => void;
  clearMemoryReports: () => void;
  reconnect: () => void;
  /** Live in-memory log buffer cap. Consumers (e.g. the device-detail
   *  page's "max" dropdown) can change this at runtime; the underlying
   *  useDeviceStream trims the buffer when it shrinks. */
  maxLogs: number;
  setMaxLogs: (n: number) => void;
  /** Internal: register/unregister a consumer's wants. Use the
   *  useSharedDeviceStream hook instead of calling this directly. */
  _registerWants: (id: string, wants: Wants) => void;
  /** Refcounted device-side feature toggles. The first consumer to
   *  request flips the device-side stream on (POSTs the enable
   *  endpoint); the last to release flips it off. Direct mutate calls
   *  bypass this and risk turning the stream off while another
   *  consumer is still watching. */
  _requestLiveLogging: (id: string, opts?: LiveLoggingOptions) => void;
  _releaseLiveLogging: (id: string) => void;
  _requestMemoryMonitor: (id: string, opts?: MemoryMonitorOptions) => void;
  _releaseMemoryMonitor: (id: string) => void;
  /** True while at least one consumer is requesting that feature. UI
   *  surfaces (toggle buttons) read this to render their on/off state
   *  consistently across the page and the dashboard. */
  liveLoggingActive: boolean;
  memoryMonitorActive: boolean;
  /** True while an enable/disable request for that feature is in flight.
   *  Toggle buttons disable on this to stop spam-clicking from firing a
   *  storm of enable/disable commands at the device. */
  liveLoggingPending: boolean;
  memoryMonitorPending: boolean;
  /** The most recent device-global feature-session transition seen on this
   *  device's socket, or null. Consumers that own their own on/off state
   *  (the activity toggle, which isn't refcounted here) watch this to fall
   *  back in step when a session ends for a reason they didn't cause. */
  lastFeatureSessionEvent: FeatureSessionEvent | null;
  /** Tell other tabs in this browser that this tab just stopped a device-global
   *  stream. The refcounted helpers call it themselves; consumers that drive a
   *  feature directly (the activity toggle) need to call it by hand. */
  _announceFeatureStopped: (feature: FeatureType) => void;
}

const DeviceStreamContext = createContext<ContextValue | null>(null);

interface DeviceStreamProviderProps {
  deviceId: string;
  /** Upper bound for the in-memory log buffer. Picked once at provider
   *  construction; tune for the hungriest consumer. Originally we had
   *  to cap below 500 because React 19's dev profiler structured-cloned
   *  the `logs` array out of the context value on every render and
   *  OOM'd past ~10k rows. The array now lives in a useRef inside
   *  useDeviceStream and is exposed via getLogs(); only logsVersion
   *  (a number) is in state and context. That eliminates the
   *  clone-OOM path, so a much higher cap (~20k) is safe — the
   *  remaining ceiling is just V8 heap. */
  maxLogs?: number;
  children: React.ReactNode;
}

export function DeviceStreamProvider({ deviceId, maxLogs: initialMaxLogs = 200, children }: DeviceStreamProviderProps) {
  // Map consumer-id → wants. useState (not useRef) so updates rerender
  // and the aggregation memos recompute.
  const [wantsMap, setWantsMap] = useState<Map<string, Wants>>(new Map());

  // Buffer cap is reactive: the device-detail page's dropdown calls
  // setMaxLogs to grow/shrink it at runtime. useDeviceStream's trim
  // effect handles shrinking the existing buffer.
  const [maxLogs, setMaxLogs] = useState<number>(initialMaxLogs);

  const anyLogs = useMemo(() => {
    for (const w of wantsMap.values()) if (w.logs) return true;
    return false;
  }, [wantsMap]);

  const anyMemory = useMemo(() => {
    for (const w of wantsMap.values()) if (w.memory) return true;
    return false;
  }, [wantsMap]);

  const anyActivity = useMemo(() => {
    for (const w of wantsMap.values()) if (w.activity) return true;
    return false;
  }, [wantsMap]);

  // Set below, once the wanter refs it reads are in scope. useDeviceStream
  // stores the callback in a ref of its own, so passing this stable wrapper
  // avoids reconnecting the socket whenever the handler identity changes.
  const featureSessionHandlerRef = useRef<(e: FeatureSessionEvent) => void>(() => {});
  const channelRef = useRef<BroadcastChannel | null>(null);
  const announceRef = useRef<(feature: FeatureType) => void>(() => {});
  // feature_type → when we last acted on a stop for it.
  const handledAtRef = useRef<Map<string, number>>(new Map());
  const onFeatureSession = useCallback((e: FeatureSessionEvent) => {
    featureSessionHandlerRef.current(e);
  }, []);

  const stream = useDeviceStream({
    deviceId,
    enabled: !!deviceId,
    enableLogs: anyLogs,
    enableMemory: anyMemory,
    maxLogs,
    onFeatureSession,
  });

  const registerWants = useCallback((id: string, wants: Wants) => {
    setWantsMap((prev) => {
      const next = new Map(prev);
      // Skip identity churn if nothing changed.
      const cur = prev.get(id);
      if (
        cur &&
        cur.logs === wants.logs &&
        cur.memory === wants.memory &&
        cur.activity === wants.activity
      ) {
        return prev;
      }
      next.set(id, wants);
      return next;
    });
  }, []);

  // ─── Device-side feature refcounting ─────────────────────────────────
  // Multiple consumers (page Live Logs button, dashboard Start button)
  // can want the same device-side feature. Without refcounting, the
  // last consumer to call disable would tear down a stream that other
  // consumers still need (the backend handler unconditionally sends
  // disableLiveLogging to the device — there's no per-device coordination
  // server-side). The provider tracks wanters per feature and only fires
  // the enable mutation on the 0→1 transition, the disable on 1→0.

  const enableLiveLoggingMut = useEnableLiveLogging(deviceId);
  const disableLiveLoggingMut = useDisableLiveLogging(deviceId);
  const enableMemoryMonitorMut = useEnableMemoryMonitor(deviceId);
  const disableMemoryMonitorMut = useDisableMemoryMonitor(deviceId);

  // React Query mutation hooks return a new object identity on every
  // render (their internal state — isPending, data, error — is part
  // of that object). If these are listed as useCallback / useMemo
  // deps, the callbacks below and the context value memo churn every
  // render too. Worse: the closures end up capturing those mutation
  // objects, and React 19's dev-mode component-render profiler tries
  // to structuredClone the whole context value into performance.measure
  // detail; the React-Query internals (queryClient ref, mutation cache)
  // are non-cloneable, and accumulated state pushes the clone over the
  // heap limit on Stop, manifesting as
  //   DataCloneError: Failed to execute 'measure' on 'Performance':
  //   Data cannot be cloned, out of memory.
  // Funnel all four through a single ref so the wrapped helper
  // callbacks below stay dep-free and stable across renders.
  const mutsRef = useRef({
    enableLiveLogging: enableLiveLoggingMut,
    disableLiveLogging: disableLiveLoggingMut,
    enableMemoryMonitor: enableMemoryMonitorMut,
    disableMemoryMonitor: disableMemoryMonitorMut,
  });
  mutsRef.current.enableLiveLogging = enableLiveLoggingMut;
  mutsRef.current.disableLiveLogging = disableLiveLoggingMut;
  mutsRef.current.enableMemoryMonitor = enableMemoryMonitorMut;
  mutsRef.current.disableMemoryMonitor = disableMemoryMonitorMut;

  // Wanter sets stored in refs so request/release callbacks stay
  // referentially stable. We mirror size into state so consumers that
  // care about activity (UI buttons) can re-render.
  const liveLogWantersRef = useRef<Set<string>>(new Set());
  const memMonitorWantersRef = useRef<Set<string>>(new Set());
  const [liveLoggingActive, setLiveLoggingActive] = useState(false);
  const [memoryMonitorActive, setMemoryMonitorActive] = useState(false);

  const requestLiveLogging = useCallback((id: string, opts?: LiveLoggingOptions) => {
    const set = liveLogWantersRef.current;
    if (set.has(id)) return;
    const wasEmpty = set.size === 0;
    set.add(id);
    if (wasEmpty) {
      mutsRef.current.enableLiveLogging.mutate({
        level: opts?.level ?? 'info',
        patchSize: opts?.patchSize,
        patchTimeout: opts?.patchTimeout,
      });
      setLiveLoggingActive(true);
    }
  }, []);

  const releaseLiveLogging = useCallback((id: string) => {
    const set = liveLogWantersRef.current;
    if (!set.has(id)) return;
    set.delete(id);
    if (set.size === 0) {
      mutsRef.current.disableLiveLogging.mutate('user_stopped');
      setLiveLoggingActive(false);
      announceRef.current('live_logging');
    }
  }, []);

  const requestMemoryMonitor = useCallback((id: string, opts?: MemoryMonitorOptions) => {
    const set = memMonitorWantersRef.current;
    if (set.has(id)) return;
    const wasEmpty = set.size === 0;
    set.add(id);
    if (wasEmpty) {
      mutsRef.current.enableMemoryMonitor.mutate({ threshold: opts?.threshold ?? 102400 });
      setMemoryMonitorActive(true);
    }
  }, []);

  const releaseMemoryMonitor = useCallback((id: string) => {
    const set = memMonitorWantersRef.current;
    if (!set.has(id)) return;
    set.delete(id);
    if (set.size === 0) {
      mutsRef.current.disableMemoryMonitor.mutate();
      setMemoryMonitorActive(false);
      announceRef.current('memory_monitor');
    }
  }, []);

  const [lastFeatureSessionEvent, setLastFeatureSessionEvent] =
    useState<FeatureSessionEvent | null>(null);

  // One channel per device, so two tabs on different devices don't hear each
  // other. BroadcastChannel never echoes to the posting tab, so the tab that
  // pressed Stop cannot notify itself.
  useEffect(() => {
    if (!deviceId || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(`${FEATURE_CHANNEL_PREFIX}${deviceId}`);
    channelRef.current = channel;
    channel.onmessage = (ev: MessageEvent<FeatureSessionEvent>) => {
      if (ev.data?.event === 'disabled') featureSessionHandlerRef.current(ev.data);
    };
    return () => {
      channel.onmessage = null;
      channel.close();
      channelRef.current = null;
    };
  }, [deviceId]);

  // Tell the other tabs in this browser that we just stopped a device-global
  // stream. Called on the 1→0 release, i.e. only when we actually asked the
  // server to stop — not when we merely dropped one of several local wanters.
  const announceStopped = useCallback(
    (feature: FeatureType) => {
      channelRef.current?.postMessage({
        event: 'disabled',
        device_id: deviceId,
        feature_type: feature,
        reason: 'manual',
        local: true,
        timestamp: new Date().toISOString(),
      } satisfies FeatureSessionEvent);
    },
    [deviceId],
  );
  useEffect(() => {
    announceRef.current = announceStopped;
  }, [announceStopped]);

  // A feature session is device-global and Stop is global with it: any tab's
  // Stop ends the stream for everyone, as do a TTL lapse, the device dropping
  // offline, and a server restart. Whatever the cause, the other tabs have to
  // hear about it — otherwise they sit on a "Stop" button in front of a feed
  // that has quietly stopped arriving.
  //
  // Only speak up when this tab is actually watching that feature, which keeps
  // the tab that pressed Stop quiet without any timestamp bookkeeping: by the
  // time the event lands its wanter set is already empty. A tab that is still
  // watching gets the notification, and its own toggle is reset by the
  // onStopped callback on the helper hooks below.
  // Synced in an effect rather than assigned during render, matching the
  // onStatusChangeRef pattern in useDeviceStream. anyActivity is the only
  // non-ref value read, so it's the only dep.
  useEffect(() => {
    featureSessionHandlerRef.current = (e: FeatureSessionEvent) => {
      if (e.event !== 'disabled') {
        setLastFeatureSessionEvent(e);
        return;
      }

      // The same Stop normally arrives twice — once from the tab next door via
      // BroadcastChannel, once from the server relay. First one wins.
      const now = Date.now();
      const last = handledAtRef.current.get(e.feature_type);
      if (last !== undefined && now - last < ANNOUNCE_DEDUPE_MS) return;
      handledAtRef.current.set(e.feature_type, now);

      setLastFeatureSessionEvent(e);

      const watching =
        (e.feature_type === 'live_logging' && liveLogWantersRef.current.size > 0) ||
        (e.feature_type === 'memory_monitor' && memMonitorWantersRef.current.size > 0) ||
        (e.feature_type === 'activity_monitor' && anyActivity);
      if (!watching) return;

      // Drop our own wanters so the toggle reflects reality — the device has
      // stopped either way, and leaving the set populated would make the next
      // Start a no-op (it only fires the enable mutation on the 0→1 transition).
      if (e.feature_type === 'live_logging') {
        liveLogWantersRef.current.clear();
        setLiveLoggingActive(false);
      } else if (e.feature_type === 'memory_monitor') {
        memMonitorWantersRef.current.clear();
        setMemoryMonitorActive(false);
      }

      notificationStore.getState().show(featureStoppedMessage(e), 'warning');
    };
  }, [anyActivity]);

  // Callback identities are now permanently stable, so they don't
  // belong in this dep list — the value only needs to churn when the
  // streaming data or the active flags change. Keeping them out also
  // shrinks the dep array the dev profiler walks per render.
  const value = useMemo<ContextValue>(() => ({
    getLogs: stream.getLogs,
    logsVersion: stream.logsVersion,
    memoryReports: stream.memoryReports,
    status: stream.status,
    isConnected: stream.isConnected,
    clearLogs: stream.clearLogs,
    clearMemoryReports: stream.clearMemoryReports,
    reconnect: stream.reconnect,
    maxLogs,
    setMaxLogs,
    _registerWants: registerWants,
    _requestLiveLogging: requestLiveLogging,
    _releaseLiveLogging: releaseLiveLogging,
    _requestMemoryMonitor: requestMemoryMonitor,
    _releaseMemoryMonitor: releaseMemoryMonitor,
    liveLoggingActive,
    memoryMonitorActive,
    lastFeatureSessionEvent,
    _announceFeatureStopped: announceStopped,
    liveLoggingPending: enableLiveLoggingMut.isPending || disableLiveLoggingMut.isPending,
    memoryMonitorPending: enableMemoryMonitorMut.isPending || disableMemoryMonitorMut.isPending,
  }), [
    stream.getLogs, stream.logsVersion,
    stream.memoryReports, stream.status, stream.isConnected,
    stream.clearLogs, stream.clearMemoryReports, stream.reconnect,
    maxLogs,
    registerWants, requestLiveLogging, releaseLiveLogging,
    requestMemoryMonitor, releaseMemoryMonitor,
    liveLoggingActive, memoryMonitorActive,
    lastFeatureSessionEvent, announceStopped,
    enableLiveLoggingMut.isPending, disableLiveLoggingMut.isPending,
    enableMemoryMonitorMut.isPending, disableMemoryMonitorMut.isPending,
  ]);

  return <DeviceStreamContext.Provider value={value}>{children}</DeviceStreamContext.Provider>;
}

/**
 * Reset hook shared by the two refcounted helpers below.
 *
 * Stop is global server-side, so a session can end while this consumer still
 * believes it is streaming — another tab's Stop, a TTL lapse, the device going
 * offline. The consumer owns its own toggle state (isLiveMode, `started`, …),
 * which the provider cannot reach, so it hands us a callback to flip it off.
 *
 * Firing it is safe even for the tab that pressed Stop: its toggle is already
 * false, and the release() it triggers is a no-op once the provider has cleared
 * the wanter set, so no second disable request goes out.
 */
function useFeatureStopped(
  event: FeatureSessionEvent | null,
  feature: FeatureType,
  onStopped?: () => void,
) {
  // Synced in an effect, not during render, so an inline arrow from the caller
  // doesn't put the callback in a dep array and re-fire on every render.
  const onStoppedRef = useRef(onStopped);
  useEffect(() => {
    onStoppedRef.current = onStopped;
  }, [onStopped]);

  useEffect(() => {
    if (event?.event === 'disabled' && event.feature_type === feature) {
      onStoppedRef.current?.();
    }
  }, [event, feature]);
}

// Helper hook: declarative "this consumer wants live logging on/off".
// Provider transitions device-side stream on the first wanter and off
// when the last consumer releases. Cleanup on unmount.
export function useDeviceLiveLogging(
  active: boolean,
  opts?: LiveLoggingOptions,
  onStopped?: () => void,
) {
  const ctx = useContext(DeviceStreamContext);
  if (!ctx) throw new Error('useDeviceLiveLogging must be used inside <DeviceStreamProvider>');
  const consumerId = useId();
  // ctx identity changes on every provider re-render (stream data
  // updates rebuild the value memo). Capture the request/release
  // callbacks in refs so the effect's dep array can be stable —
  // otherwise we'd cleanup-then-rerun the wanter cycle on every WS
  // frame and bounce the wanter set through zero, triggering enable/
  // disable mutations in a loop.
  const requestRef = useRef(ctx._requestLiveLogging);
  const releaseRef = useRef(ctx._releaseLiveLogging);
  requestRef.current = ctx._requestLiveLogging;
  releaseRef.current = ctx._releaseLiveLogging;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  useEffect(() => {
    if (active) requestRef.current(consumerId, optsRef.current);
    else releaseRef.current(consumerId);
    return () => releaseRef.current(consumerId);
  }, [active, consumerId]);
  useFeatureStopped(ctx.lastFeatureSessionEvent, 'live_logging', onStopped);
  return { active: ctx.liveLoggingActive, pending: ctx.liveLoggingPending };
}

export function useDeviceMemoryMonitor(
  active: boolean,
  opts?: MemoryMonitorOptions,
  onStopped?: () => void,
) {
  const ctx = useContext(DeviceStreamContext);
  if (!ctx) throw new Error('useDeviceMemoryMonitor must be used inside <DeviceStreamProvider>');
  const consumerId = useId();
  const requestRef = useRef(ctx._requestMemoryMonitor);
  const releaseRef = useRef(ctx._releaseMemoryMonitor);
  requestRef.current = ctx._requestMemoryMonitor;
  releaseRef.current = ctx._releaseMemoryMonitor;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  useEffect(() => {
    if (active) requestRef.current(consumerId, optsRef.current);
    else releaseRef.current(consumerId);
    return () => releaseRef.current(consumerId);
  }, [active, consumerId]);
  useFeatureStopped(ctx.lastFeatureSessionEvent, 'memory_monitor', onStopped);
  return { active: ctx.memoryMonitorActive, pending: ctx.memoryMonitorPending };
}

export interface UseSharedDeviceStreamOptions {
  /** Whether this consumer wants log frames delivered. Aggregated with
   *  other consumers' wants — the WS turns logs on when at least one
   *  consumer wants them, off when nobody does. */
  logs?: boolean;
  /** Same as `logs` but for memory-report frames. */
  memory?: boolean;
  /** Whether this consumer is watching activity frames. Activity rides the
   *  `logs` gate for delivery, so setting this alone delivers nothing — pass
   *  `logs` too. Its job is to register this tab as an activity watcher so a
   *  Stop elsewhere doesn't tear the device-side monitor down under us. */
  activity?: boolean;
}

export function useSharedDeviceStream(opts: UseSharedDeviceStreamOptions = {}) {
  const ctx = useContext(DeviceStreamContext);
  if (!ctx) {
    throw new Error('useSharedDeviceStream must be used inside <DeviceStreamProvider>');
  }
  const consumerId = useId();
  const wantLogs = !!opts.logs;
  const wantMemory = !!opts.memory;
  const wantActivity = !!opts.activity;

  // Stash the registration callback in a ref so the effect doesn't
  // re-run every time the provider re-renders (which is on every WS
  // frame). Re-running the cleanup → effect cycle on every frame
  // toggled the wanter set through 0 and triggered an infinite render
  // loop. The function identity is logically stable; the ref keeps it
  // out of the dep array.
  const registerRef = useRef(ctx._registerWants);
  registerRef.current = ctx._registerWants;

  useEffect(() => {
    registerRef.current(consumerId, {
      logs: wantLogs,
      memory: wantMemory,
      activity: wantActivity,
    });
    return () => {
      // Releasing wants on unmount lets the provider drop the WS feed
      // when nobody's consuming it anymore.
      registerRef.current(consumerId, { logs: false, memory: false, activity: false });
    };
  }, [consumerId, wantLogs, wantMemory, wantActivity]);

  return {
    getLogs: ctx.getLogs,
    logsVersion: ctx.logsVersion,
    memoryReports: ctx.memoryReports,
    status: ctx.status,
    isConnected: ctx.isConnected,
    clearLogs: ctx.clearLogs,
    clearMemoryReports: ctx.clearMemoryReports,
    reconnect: ctx.reconnect,
  };
}

/**
 * Announce to the other tabs in this browser that this tab just stopped a
 * device-global stream.
 *
 * The two refcounted helpers do this for themselves. The activity monitor drives
 * its own enable/disable mutations instead of going through the provider, so it
 * has to say so explicitly.
 */
export function useAnnounceFeatureStopped(): (feature: FeatureType) => void {
  const ctx = useContext(DeviceStreamContext);
  if (!ctx) {
    throw new Error('useAnnounceFeatureStopped must be used inside <DeviceStreamProvider>');
  }
  return ctx._announceFeatureStopped;
}

/**
 * The last feature-session transition seen on this device's socket.
 *
 * For consumers that keep their own on/off state instead of the provider's
 * refcount — the activity toggle — so they can drop back to "off" when the
 * device-side session ends for a reason they didn't cause. The provider has
 * already shown the notification by then; this is only about the button.
 */
export function useDeviceFeatureSessionEvent(): FeatureSessionEvent | null {
  const ctx = useContext(DeviceStreamContext);
  if (!ctx) {
    throw new Error('useDeviceFeatureSessionEvent must be used inside <DeviceStreamProvider>');
  }
  return ctx.lastFeatureSessionEvent;
}

// Live log buffer cap. Reading and updating share the same provider
// state so the dropdown's selection is the source of truth for the
// in-memory cap (and matches what's shown in the "Lines: N" footer).
export function useDeviceStreamBufferCap(): readonly [number, (n: number) => void] {
  const ctx = useContext(DeviceStreamContext);
  if (!ctx) {
    throw new Error('useDeviceStreamBufferCap must be used inside <DeviceStreamProvider>');
  }
  return [ctx.maxLogs, ctx.setMaxLogs] as const;
}
